package com.kanati.admin.features.events

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.admin.state.browserTimeZoneLabel
import com.kanati.admin.state.formatLocalDateInputFromIso
import com.kanati.admin.state.formatMoney
import com.kanati.admin.state.formatLocalDateTimeDisplay
import com.kanati.admin.state.formatLocalTimeInputFromIso
import com.kanati.admin.state.parseLocalDateAndTimeInputToIso
import com.kanati.shared.event.CreateEventRequest
import com.kanati.shared.event.CreateTicketTierRequest
import com.kanati.shared.event.EventDto
import com.kanati.shared.event.EventStatsDto
import com.kanati.shared.event.EventTicketTierStatsDto
import com.kanati.shared.event.TicketTierDto
import com.kanati.shared.event.UpdateEventRequest
import com.kanati.shared.event.UpdateTicketTierRequest
import com.kanati.shared.ticket.CreatePaperTicketBatchRequest
import com.kanati.shared.ticket.CreatePaperTicketBatchResponse
import com.kanati.shared.ticket.IssueTicketRequest
import com.kanati.shared.ticket.PaperTicketBatchDto
import com.kanati.shared.ticket.ReleasePaperTicketBatchRequest
import com.kanati.shared.ticket.ValidateTicketRequest
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.attributes.selected
import kotlinx.coroutines.CancellationException
import com.kanati.shared.ticket.TicketDto
import com.kanati.shared.ticket.TicketEntitlementDto
import com.kanati.shared.ticket.TicketEntitlementType
import com.kanati.shared.ticket.TicketStatus
import kotlinx.coroutines.launch
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Date
import kotlin.math.abs
import kotlin.math.roundToInt
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement

private enum class EventFilter(val label: String) {
    Active("Upcoming/Active"),
    Archived("Archived"),
    All("All"),
}

private data class ManageEventDialogState(
    val event: EventDto,
)

private data class TicketEntitlementDraft(
    val type: TicketEntitlementType = TicketEntitlementType.CONCESSION_CREDIT,
    val label: String = "",
    val amountInput: String = "",
    val quantityInput: String = "",
)

private data class TicketTierDraft(
    val id: String? = null,
    val name: String = "",
    val priceInput: String = "",
    val capacityInput: String = "",
    val active: Boolean = true,
    val entitlements: List<TicketEntitlementDraft> = emptyList(),
)

private data class CapacityDialogState(
    val currentCapacity: Int?,
    val initialDraft: String,
)

private enum class TimeMeridiem {
    AM,
    PM,
}

private data class TimePickerDraft(
    val hour: Int? = null,
    val minute: Int? = null,
    val meridiem: TimeMeridiem = TimeMeridiem.AM,
)

private data class WheelOption<T>(
    val value: T,
    val label: String,
)

private const val TimeWheelRowHeightPx = 52
private const val TimeWheelVisibleRows = 5
private const val TimeWheelSidePaddingRows = 2

@Composable
fun EventsScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    school: SelectedSchoolContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    canEdit: Boolean,
    canValidate: Boolean,
) {
    val scope = rememberCoroutineScope()
    val localTimeZoneLabel = remember { browserTimeZoneLabel() }
    var events by remember { mutableStateOf<List<EventDto>>(emptyList()) }
    var isRefreshing by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }

    var createError by remember { mutableStateOf<String?>(null) }
    var createSuccess by remember { mutableStateOf<String?>(null) }
    var draftName by remember { mutableStateOf("") }
    var draftLocation by remember { mutableStateOf("") }
    var draftStartDate by remember { mutableStateOf("") }
    var draftStartTime by remember { mutableStateOf(TimePickerDraft()) }
    var draftEndDate by remember { mutableStateOf("") }
    var draftEndTime by remember { mutableStateOf(TimePickerDraft()) }
    var draftCapacity by remember { mutableStateOf<Int?>(null) }
    var draftTicketTiers by remember { mutableStateOf(listOf(defaultTicketTierDraft())) }

    var validationEventId by remember { mutableStateOf("") }
    var validationCode by remember { mutableStateOf("") }
    var validationError by remember { mutableStateOf<String?>(null) }
    var validationSuccess by remember { mutableStateOf<String?>(null) }
    var isValidating by remember { mutableStateOf(false) }

    var manageDialog by remember { mutableStateOf<ManageEventDialogState?>(null) }
    var eventFilter by remember { mutableStateOf(EventFilter.Active) }

    fun sortedEvents(items: List<EventDto>): List<EventDto> =
        items.sortedBy { it.startDate }

    fun mergeEvent(updated: EventDto) {
        var replaced = false
        events = sortedEvents(
            events.map { existing ->
                if (existing.id == updated.id) {
                    replaced = true
                    updated
                } else {
                    existing
                }
            }.let { current -> if (replaced) current else current + updated }
        )
    }

    suspend fun refreshEvents(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
    ) {
        isRefreshing = true
        loadError = null
        events = emptyList()
        try {
            events = sortedEvents(apiClient.getEvents(selectedDistrict.id, selectedSchool.id))
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load events."
        } finally {
            isRefreshing = false
        }
    }

    LaunchedEffect(district?.id, school?.id) {
        if (district != null && school != null) {
            refreshEvents(district, school)
        } else {
            events = emptyList()
        }
    }

    fun submitValidation() {
        val selectedDistrict = district ?: return
        val selectedSchool = school ?: return
        if (validationCode.isBlank() || isValidating || !canValidate || events.none { it.id == validationEventId }) return
        val selectedEventId = validationEventId
        val code = validationCode.trim()

        scope.launch {
            isValidating = true
            validationError = null
            validationSuccess = null
            try {
                val response = apiClient.validateTicket(
                    districtId = selectedDistrict.id,
                    schoolId = selectedSchool.id,
                    eventId = selectedEventId,
                    request = ValidateTicketRequest(ticketCode = code),
                )
                if (response.isOnlineAdmission()) {
                    validationSuccess = response.entryMessage()
                    validationCode = ""
                } else {
                    validationError = response.entryMessage()
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                validationError = "Validation unavailable. No admission is confirmed; pause entry and check connectivity or POS access. A failed response does not prove the ticket is unused."
            } finally {
                isValidating = false
            }
        }
    }

    fun submitCreateEvent() {
        val selectedDistrict = district ?: return
        val selectedSchool = school ?: return
        val ticketTierRequests = draftTicketTiers.toCreateRequests() ?: return
        val startTimeValue = draftStartTime.toLocalTimeValueOrNull()
        val endTimeValue = draftEndTime.toLocalTimeValueOrNull()
        val startDateIso = startTimeValue?.let { parseLocalDateAndTimeInputToIso(draftStartDate, it) }
        val endDateIso = endTimeValue?.let { parseLocalDateAndTimeInputToIso(draftEndDate, it) }
        if (
            draftName.isBlank() ||
            draftLocation.isBlank() ||
            draftStartDate.isBlank() ||
            !draftStartTime.isComplete() ||
            draftEndDate.isBlank() ||
            !draftEndTime.isComplete() ||
            startDateIso == null ||
            endDateIso == null
        ) return

        eventTimeError(startDateIso, endDateIso)?.let { createError = it; return }

        scope.launch {
            createError = null
            createSuccess = null
            isSubmitting = true
            try {
                val created = apiClient.createEvent(
                    CreateEventRequest(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        name = draftName,
                        startDate = startDateIso,
                        endDate = endDateIso,
                        location = draftLocation,
                        capacity = draftCapacity,
                        ticketTiers = ticketTierRequests,
                    )
                )
                mergeEvent(created)
                createSuccess = "Event '${created.name}' created."
                draftName = ""
                draftLocation = ""
                draftStartDate = ""
                draftStartTime = TimePickerDraft()
                draftEndDate = ""
                draftEndTime = TimePickerDraft()
                draftCapacity = null
                draftTicketTiers = listOf(defaultTicketTierDraft())
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                createError = exception.message ?: "Unable to create event."
            } finally {
                isSubmitting = false
            }
        }
    }

    val visibleEvents = events.filter { event ->
        when (eventFilter) {
            EventFilter.Active -> event.active
            EventFilter.Archived -> !event.active
            EventFilter.All -> true
        }
    }
    val createMissingRequirements = remember(
        draftName,
        draftLocation,
        draftStartDate,
        draftStartTime,
        draftEndDate,
        draftEndTime,
        draftTicketTiers,
    ) {
        buildCreateEventMissingRequirements(
            eventName = draftName,
            location = draftLocation,
            startDate = draftStartDate,
            startTime = draftStartTime,
            endDate = draftEndDate,
            endTime = draftEndTime,
            ticketTiers = draftTicketTiers,
        )
    }
    val createButtonDisabled = !canEdit || isSubmitting || createMissingRequirements.isNotEmpty()

    if (district == null || school == null || districtLoading || districtLoadError != null) {
        Div({ style { padding(24.px) } }) {
            P({ style { property("color", theme.onSurfaceVariant) } }) {
                Text(
                    when {
                        districtLoading -> "Loading district context..."
                        districtLoadError != null -> "District context could not be loaded."
                        else -> "Select a district and school to manage events."
                    }
                )
            }
        }
        return
    }

    Div({
        style {
            padding(24.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(24.px)
        }
    }) {
        SectionCard(
            title = "Events",
            description = "Configure events, ticket tiers, prices, and capacity for ${school.name}. School ID: ${school.id}.",
            theme = theme,
            headerActions = {
                ActionButton(if (isRefreshing) "Loading..." else "Refresh events", { scope.launch { refreshEvents(district, school) } }, theme, isDisabled = isRefreshing, isPrimary = false)
                EventFilter.entries.forEach { filter ->
                    ChoiceChip(
                        label = filter.label,
                        isSelected = eventFilter == filter,
                        onClick = { eventFilter = filter },
                        theme = theme,
                    )
                }
            },
        ) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                }
            }) {
                if (isRefreshing) StatusBanner("Loading events...", theme)
                loadError?.let { StatusBanner(it, tone = BannerTone.Error, theme = theme) }
                if (visibleEvents.isEmpty() && !isRefreshing && loadError == null) {
                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                        Text("No events found for the selected filter.")
                    }
                } else {
                    visibleEvents.forEach { event ->
                        Div({
                            style {
                                padding(18.px)
                                borderRadius(24.px)
                                property("border", "1px solid ${theme.outlineVariant}")
                                property("background-color", if (event.active) theme.surface else theme.surfaceVariant)
                                display(DisplayStyle.Flex)
                                property("justify-content", "space-between")
                                property("align-items", "flex-start")
                                property("gap", "16px")
                                property("flex-wrap", "wrap")
                            }
                        }) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(8.px)
                                    property("max-width", "720px")
                                }
                            }) {
                                H3({ style { margin(0.px); property("color", theme.onSurface) } }) {
                                    Text(event.name)
                                    if (!event.active) {
                                        Text(" (Archived)")
                                    }
                                }
                                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                    Text("${formatLocalDateTimeDisplay(event.startDate)} · ${event.location} · ${formatStartingPrice(event)} · ${formatCapacitySummary(event.capacity)}")
                                }
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        gap(8.px)
                                        property("flex-wrap", "wrap")
                                    }
                                }) {
                                    event.ticketTiers.forEach { ticketTier ->
                                        ChoiceChip(
                                            label = tierSummary(ticketTier),
                                            isSelected = ticketTier.active,
                                            onClick = {},
                                            theme = theme,
                                            isInteractive = false,
                                        )
                                    }
                                }
                            }
                            ActionButton(
                                label = "Manage",
                                isDisabled = !canEdit,
                                onClick = { manageDialog = ManageEventDialogState(event) },
                                theme = theme,
                                isPrimary = false,
                            )
                        }
                    }
                }
            }
        }

        SectionCard(
            title = "Create Event",
            description = "Create an event for ${school.name}. Set its schedule, location, and active ticket tiers for Consumer and POS.",
            theme = theme,
        ) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                createError?.let { StatusBanner(it, tone = BannerTone.Error, theme = theme) }
                createSuccess?.let { StatusBanner(it, tone = BannerTone.Success, theme = theme) }

                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Event Name",
                        value = draftName,
                        onValueChange = { draftName = it },
                        placeholder = "Friday Night Football",
                    )
                    TextField(
                        theme = theme,
                        label = "Location",
                        value = draftLocation,
                        onValueChange = { draftLocation = it },
                        placeholder = "Main Stadium",
                    )
                }
                FieldRow {
                    LargePickerField(
                        theme = theme,
                        label = "Start Date",
                        value = draftStartDate,
                        onValueChange = { draftStartDate = it },
                        inputTypeOverride = "date",
                    )
                    AlarmTimePickerField(
                        theme = theme,
                        label = "Start Time",
                        value = draftStartTime,
                        onValueChange = { draftStartTime = it },
                    )
                }
                FieldRow {
                    LargePickerField(
                        theme = theme,
                        label = "End Date",
                        value = draftEndDate,
                        onValueChange = { draftEndDate = it },
                        inputTypeOverride = "date",
                    )
                    AlarmTimePickerField(
                        theme = theme,
                        label = "End Time",
                        value = draftEndTime,
                        onValueChange = { draftEndTime = it },
                    )
                }
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text("Dates and times use your current local time zone: $localTimeZoneLabel. Tap the time field to open the picker wheel.")
                }
                CapacityField(
                    theme = theme,
                    label = "Event Capacity",
                    capacity = draftCapacity,
                    supportingText = "Use + / - for small changes. Tap the value to set an exact cap or explicitly switch back to Unlimited.",
                    dialogTitle = "Set Event Capacity",
                    dialogSubjectLabel = "event",
                    onCapacityChange = { draftCapacity = it },
                )

                TicketTierEditor(
                    title = "Ticket Tiers",
                    drafts = draftTicketTiers,
                    theme = theme,
                    onDraftsChange = { draftTicketTiers = it },
                )

                CreateEventRequirementsPanel(
                    theme = theme,
                    missingRequirements = createMissingRequirements,
                )

                ActionButton(
                    label = if (isSubmitting) "Creating..." else "Create Event",
                    onClick = { submitCreateEvent() },
                    isDisabled = createButtonDisabled,
                    theme = theme,
                )
            }
        }

        SectionCard(
            title = "Online manual entry",
            description = "Select the event before validating. A successful validation consumes the ticket. Use POS or gate tooling for routine scanning; offline admission and refunds are not available for launch.",
            theme = theme,
        ) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                if (!canValidate) StatusBanner("POS access to this school is required for manual entry validation.", theme)
                Select(attrs = {
                    attr("aria-label", "Event for entry validation")
                    if (isValidating || !canValidate) disabled()
                    onChange { validationEventId = it.value.orEmpty(); validationError = null; validationSuccess = null }
                }) {
                    Option("", attrs = { if (validationEventId.isBlank()) selected() }) { Text("Select event for admission") }
                    events.forEach { event ->
                        Option(event.id, attrs = { if (validationEventId == event.id) selected() }) { Text("${event.name} · ${formatLocalDateTimeDisplay(event.startDate)}") }
                    }
                }
                validationError?.let { StatusBanner(it, tone = BannerTone.Error, theme = theme) }
                validationSuccess?.let { StatusBanner(it, tone = BannerTone.Success, theme = theme) }
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Ticket Code",
                        value = validationCode,
                        onValueChange = { if (!isValidating) { validationCode = it; validationError = null; validationSuccess = null } },
                        placeholder = "Scan or paste a ticket code",
                    )
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        property("flex-wrap", "wrap")
                    }
                }) {
                    ActionButton(
                        label = if (isValidating) "Validating..." else "Validate Entry",
                        onClick = { submitValidation() },
                        isDisabled = isValidating || !canValidate || validationCode.isBlank() || events.none { it.id == validationEventId },
                        theme = theme,
                    )
                    ActionButton(
                        label = "Clear",
                        onClick = {
                            validationCode = ""
                            validationError = null
                            validationSuccess = null
                        },
                        theme = theme,
                        isPrimary = false,
                        isDisabled = isValidating,
                    )
                }
            }
        }
    }

    manageDialog?.let { dialog ->
        ManageEventDialog(
            apiClient = apiClient,
            districtId = district.id,
            event = dialog.event,
            theme = theme,
            onClose = { manageDialog = null },
            onUpdate = { updated ->
                mergeEvent(updated)
                manageDialog = ManageEventDialogState(updated)
            },
        )
    }
}

@Composable
private fun ManageEventDialog(
    apiClient: AdminApiClient,
    districtId: String,
    event: EventDto,
    theme: MaterialThemePalette,
    onClose: () -> Unit,
    onUpdate: (EventDto) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val localTimeZoneLabel = remember { browserTimeZoneLabel() }
    var currentEvent by remember { mutableStateOf(event) }
    var stats by remember { mutableStateOf<EventStatsDto?>(null) }
    var issuedTickets by remember { mutableStateOf<List<TicketDto>>(emptyList()) }
    var paperBatches by remember { mutableStateOf<List<PaperTicketBatchDto>>(emptyList()) }
    var ticketLoadError by remember { mutableStateOf<String?>(null) }
    var isLoadingStats by remember { mutableStateOf(true) }

    var saveError by remember { mutableStateOf<String?>(null) }
    var saveSuccess by remember { mutableStateOf<String?>(null) }
    var draftName by remember { mutableStateOf(event.name) }
    var draftLocation by remember { mutableStateOf(event.location) }
    var draftStartDate by remember { mutableStateOf(formatLocalDateInputFromIso(event.startDate)) }
    var draftStartTime by remember { mutableStateOf(timePickerDraftFromIso(event.startDate)) }
    var draftEndDate by remember { mutableStateOf(formatLocalDateInputFromIso(event.endDate)) }
    var draftEndTime by remember { mutableStateOf(timePickerDraftFromIso(event.endDate)) }
    var draftCapacity by remember { mutableStateOf(event.capacity) }
    var draftActive by remember { mutableStateOf(event.active) }
    var draftTicketTiers by remember { mutableStateOf(event.ticketTiers.map { it.toDraft() }) }
    var isSaving by remember { mutableStateOf(false) }

    var issueError by remember { mutableStateOf<String?>(null) }
    var issueSuccess by remember { mutableStateOf<TicketDto?>(null) }
    var issueHolderName by remember { mutableStateOf("") }
    var selectedIssueTierId by remember { mutableStateOf(currentEvent.ticketTiers.firstOrNull { it.active }?.id) }
    var isIssuing by remember { mutableStateOf(false) }
    var paperBatchError by remember { mutableStateOf<String?>(null) }
    var paperBatchSuccess by remember { mutableStateOf<CreatePaperTicketBatchResponse?>(null) }
    var paperBatchReleaseSuccess by remember { mutableStateOf<String?>(null) }
    var paperBatchQuantity by remember { mutableStateOf("25") }
    var selectedPaperTierId by remember { mutableStateOf(currentEvent.ticketTiers.firstOrNull { it.active }?.id) }
    var isCreatingPaperBatch by remember { mutableStateOf(false) }
    var releasingBatchId by remember { mutableStateOf<String?>(null) }
    var releaseQuantities by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    fun syncDrafts(updated: EventDto) {
        currentEvent = updated
        draftName = updated.name
        draftLocation = updated.location
        draftStartDate = formatLocalDateInputFromIso(updated.startDate)
        draftStartTime = timePickerDraftFromIso(updated.startDate)
        draftEndDate = formatLocalDateInputFromIso(updated.endDate)
        draftEndTime = timePickerDraftFromIso(updated.endDate)
        draftCapacity = updated.capacity
        draftActive = updated.active
        draftTicketTiers = updated.ticketTiers.map { it.toDraft() }
        if (updated.ticketTiers.none { it.id == selectedIssueTierId && it.active }) {
            selectedIssueTierId = updated.ticketTiers.firstOrNull { it.active }?.id
        }
        if (updated.ticketTiers.none { it.id == selectedPaperTierId && it.active }) {
            selectedPaperTierId = updated.ticketTiers.firstOrNull { it.active }?.id
        }
    }

    suspend fun loadTicketData() {
        isLoadingStats = true
        ticketLoadError = null
        stats = null
        issuedTickets = emptyList()
        paperBatches = emptyList()
        try {
            stats = apiClient.getEventStats(districtId, currentEvent.schoolId, currentEvent.id)
            issuedTickets = apiClient.getIssuedTickets(districtId, currentEvent.schoolId, currentEvent.id).tickets
            paperBatches = apiClient.getPaperTicketBatches(districtId, currentEvent.schoolId, currentEvent.id).batches
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Exception) {
            ticketLoadError = "Ticket details could not be loaded. Counts and paper stock are unknown. Retry before issuing, reserving, releasing, or exporting tickets."
            stats = null
            issuedTickets = emptyList()
            paperBatches = emptyList()
        } finally {
            isLoadingStats = false
        }
    }

    LaunchedEffect(currentEvent.id) {
        loadTicketData()
    }

    fun submitSave() {
        val ticketTierRequests = draftTicketTiers.toUpdateRequests() ?: return
        val startTimeValue = draftStartTime.toLocalTimeValueOrNull()
        val endTimeValue = draftEndTime.toLocalTimeValueOrNull()
        val startDateIso = startTimeValue?.let { parseLocalDateAndTimeInputToIso(draftStartDate, it) }
        val endDateIso = endTimeValue?.let { parseLocalDateAndTimeInputToIso(draftEndDate, it) }
        if (
            draftName.isBlank() ||
            draftLocation.isBlank() ||
            draftStartDate.isBlank() ||
            !draftStartTime.isComplete() ||
            draftEndDate.isBlank() ||
            !draftEndTime.isComplete() ||
            startDateIso == null ||
            endDateIso == null ||
            isSaving
        ) return

        eventTimeError(startDateIso, endDateIso)?.let { saveError = it; return }

        scope.launch {
            isSaving = true
            saveError = null
            saveSuccess = null
            try {
                val updated = apiClient.updateEvent(
                    eventId = currentEvent.id,
                    request = UpdateEventRequest(
                        districtId = districtId,
                        schoolId = currentEvent.schoolId,
                        name = draftName,
                        startDate = startDateIso,
                        endDate = endDateIso,
                        location = draftLocation,
                        capacity = draftCapacity,
                        replaceCapacity = true,
                        ticketTiers = ticketTierRequests,
                        active = draftActive,
                    ),
                )
                syncDrafts(updated)
                onUpdate(updated)
                loadTicketData()
                saveSuccess = "Event details saved."
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                saveError = exception.message ?: "Unable to save event."
            } finally {
                isSaving = false
            }
        }
    }

    fun submitArchiveToggle() {
        if (isSaving) return
        scope.launch {
            isSaving = true
            saveError = null
            saveSuccess = null
            try {
                val updated = apiClient.updateEvent(
                    eventId = currentEvent.id,
                    request = UpdateEventRequest(
                        districtId = districtId,
                        schoolId = currentEvent.schoolId,
                        name = null,
                        startDate = null,
                        endDate = null,
                        location = null,
                        capacity = null,
                        replaceCapacity = false,
                        ticketTiers = null,
                        active = !draftActive,
                    ),
                )
                syncDrafts(updated)
                onUpdate(updated)
                loadTicketData()
                saveSuccess = if (updated.active) "Event restored." else "Event archived."
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                saveError = exception.message ?: "Unable to update event state."
            } finally {
                isSaving = false
            }
        }
    }

    fun submitIssue() {
        val ticketTierId = selectedIssueTierId ?: return
        if (isIssuing) return
        scope.launch {
            isIssuing = true
            issueError = null
            issueSuccess = null
            try {
                val ticket = apiClient.issueTicket(
                    districtId = districtId,
                    schoolId = currentEvent.schoolId,
                    eventId = currentEvent.id,
                    request = IssueTicketRequest(
                        eventId = currentEvent.id,
                        districtId = districtId,
                        schoolId = currentEvent.schoolId,
                        ticketTierId = ticketTierId,
                        holderName = issueHolderName.ifBlank { null },
                    ),
                )
                issueSuccess = ticket
                loadTicketData()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                issueError = exception.message ?: "Unable to issue ticket."
            } finally {
                isIssuing = false
            }
        }
    }

    fun submitPaperBatch() {
        val ticketTierId = selectedPaperTierId ?: return
        val quantity = paperBatchQuantity.toIntOrNull() ?: return
        if (isCreatingPaperBatch || quantity < 1) return
        scope.launch {
            isCreatingPaperBatch = true
            paperBatchError = null
            paperBatchSuccess = null
            paperBatchReleaseSuccess = null
            try {
                val batch = apiClient.createPaperTicketBatch(
                    districtId = districtId,
                    schoolId = currentEvent.schoolId,
                    eventId = currentEvent.id,
                    request = CreatePaperTicketBatchRequest(
                        eventId = currentEvent.id,
                        districtId = districtId,
                        schoolId = currentEvent.schoolId,
                        ticketTierId = ticketTierId,
                        quantity = quantity,
                    ),
                )
                paperBatchSuccess = batch
                releaseQuantities = releaseQuantities + (batch.batch.id to batch.batch.remainingQuantity.toString())
                loadTicketData()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                paperBatchError = exception.message ?: "Unable to reserve paper ticket batch."
            } finally {
                isCreatingPaperBatch = false
            }
        }
    }

    fun submitPaperRelease(batch: PaperTicketBatchDto) {
        val releaseQuantity = (releaseQuantities[batch.id] ?: batch.remainingQuantity.toString()).toIntOrNull() ?: return
        if (releasingBatchId != null || releaseQuantity < 1) return
        scope.launch {
            releasingBatchId = batch.id
            paperBatchError = null
            paperBatchReleaseSuccess = null
            try {
                val updatedBatch = apiClient.releasePaperTicketBatch(
                    districtId = districtId,
                    schoolId = currentEvent.schoolId,
                    eventId = currentEvent.id,
                    batchId = batch.id,
                    request = ReleasePaperTicketBatchRequest(releaseQuantity = releaseQuantity),
                )
                releaseQuantities = releaseQuantities + (batch.id to updatedBatch.remainingQuantity.toString())
                paperBatchReleaseSuccess =
                    "Released $releaseQuantity unused paper tickets from ${updatedBatch.ticketTierName} batch ${updatedBatch.id.take(8)}."
                loadTicketData()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (exception: Exception) {
                paperBatchError = exception.message ?: "Unable to release paper ticket stock."
            } finally {
                releasingBatchId = null
            }
        }
    }

    fun exportBatchCsv(batch: PaperTicketBatchDto) {
        val batchTickets = issuedTickets.filter { it.paperBatchId == batch.id }
        if (batchTickets.isEmpty()) return
        downloadCsv(
            filename = "${currentEvent.name.slugifyForDownload()}-${batch.ticketTierName.slugifyForDownload()}-${batch.id.take(8)}.csv",
            rows = batchTickets.map { ticket ->
                mapOf(
                    "batch_id" to batch.id,
                    "ticket_code" to ticket.ticketCode,
                    "status" to formatTicketStatusLabel(ticket.status),
                    "ticket_tier" to ticket.ticketTierName,
                    "holder_name" to (ticket.holderName ?: ""),
                    "created_at" to ticket.createdAt,
                )
            },
        )
    }

    fun exportIssuedTicketsCsv() {
        if (issuedTickets.isEmpty()) return
        downloadCsv(
            filename = "${currentEvent.name.slugifyForDownload()}-issued-tickets.csv",
            rows = issuedTickets.map { ticket ->
                mapOf(
                    "ticket_code" to ticket.ticketCode,
                    "status" to formatTicketStatusLabel(ticket.status),
                    "ticket_tier" to ticket.ticketTierName,
                    "paper_batch_id" to (ticket.paperBatchId ?: ""),
                    "holder_name" to (ticket.holderName ?: ""),
                    "created_at" to ticket.createdAt,
                )
            },
        )
    }

    val draftStartIso = draftStartTime.toLocalTimeValueOrNull()?.let {
        parseLocalDateAndTimeInputToIso(draftStartDate, it)
    }
    val draftEndIso = draftEndTime.toLocalTimeValueOrNull()?.let {
        parseLocalDateAndTimeInputToIso(draftEndDate, it)
    }
    val canSave = !isSaving &&
        draftName.isNotBlank() &&
        draftLocation.isNotBlank() &&
        draftStartDate.isNotBlank() &&
        draftStartIso != null &&
        draftEndDate.isNotBlank() &&
        draftEndIso != null &&
        eventTimeError(draftStartIso, draftEndIso) == null &&
        draftTicketTiers.canSubmit()
    val eventStateLabel = if (draftActive) "Event Active" else "Event Archived"
    val eventSummary = "${formatLocalDateTimeDisplay(currentEvent.startDate)} · ${currentEvent.location}"

    Div({
        style {
            property("position", "fixed")
            property("inset", "0")
            property("background-color", "rgba(15, 23, 42, 0.46)")
            display(DisplayStyle.Flex)
            property("justify-content", "center")
            property("align-items", "center")
            property("z-index", "1000")
            padding(18.px)
        }
    }) {
        Div({
            style {
                property("background-color", theme.surfaceContainerHigh)
                borderRadius(24.px)
                property("border", "1px solid ${theme.outline}")
                property("box-shadow", "0 24px 56px ${theme.shadow}")
                property("width", "min(960px, 100%)")
                property("max-height", "92vh")
                property("overflow", "hidden")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("min-height", "0")
            }
        }) {
            Div({
                style {
                    property("padding", "24px 24px 0")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                }
            }) {
                H3({
                    style {
                        margin(0.px)
                        property("color", theme.onSurface)
                    }
                }) {
                    Text(currentEvent.name)
                }
                P({
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("line-height", "1.5")
                    }
                }) {
                    Text(eventSummary)
                }
            }

            Div({
                style {
                    property("padding", "18px 24px 0")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                    property("flex", "1 1 auto")
                    property("overflow-y", "auto")
                    property("min-height", "0")
                    property("scrollbar-width", "thin")
                }
            }) {
                SectionCard(
                    title = "Event Details",
                    description = "Keep the event details readable here while you configure the tiers operators will sell against.",
                    headerActions = {
                        ChoiceChip(
                            label = eventStateLabel,
                            isSelected = draftActive,
                            onClick = { draftActive = !draftActive },
                            theme = theme,
                        )
                    },
                    theme = theme,
                ) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(14.px)
                        }
                    }) {
                        saveError?.let { StatusBanner(it, tone = BannerTone.Error, theme = theme) }
                        saveSuccess?.let { StatusBanner(it, tone = BannerTone.Success, theme = theme) }

                        FieldRow {
                            TextField(
                                theme = theme,
                                label = "Event Name",
                                value = draftName,
                                onValueChange = { draftName = it },
                            )
                            TextField(
                                theme = theme,
                                label = "Location",
                                value = draftLocation,
                                onValueChange = { draftLocation = it },
                            )
                        }
                        FieldRow {
                            LargePickerField(
                                theme = theme,
                                label = "Start Date",
                                value = draftStartDate,
                                onValueChange = { draftStartDate = it },
                                inputTypeOverride = "date",
                            )
                            AlarmTimePickerField(
                                theme = theme,
                                label = "Start Time",
                                value = draftStartTime,
                                onValueChange = { draftStartTime = it },
                            )
                        }
                        FieldRow {
                            LargePickerField(
                                theme = theme,
                                label = "End Date",
                                value = draftEndDate,
                                onValueChange = { draftEndDate = it },
                                inputTypeOverride = "date",
                            )
                            AlarmTimePickerField(
                                theme = theme,
                                label = "End Time",
                                value = draftEndTime,
                                onValueChange = { draftEndTime = it },
                            )
                        }
                        P({
                            style {
                                margin(0.px)
                                property("color", theme.onSurfaceVariant)
                                property("line-height", "1.5")
                            }
                        }) {
                            Text("Dates and times use your current local time zone: $localTimeZoneLabel. Tap the time field to open the picker wheel.")
                        }
                        CapacityField(
                            theme = theme,
                            label = "Event Capacity",
                            capacity = draftCapacity,
                            supportingText = "Use + / - for small changes. Tap the value to set an exact cap or explicitly switch back to Unlimited.",
                            dialogTitle = "Set Event Capacity",
                            dialogSubjectLabel = "event",
                            onCapacityChange = { draftCapacity = it },
                        )

                        TicketTierEditor(
                            title = "Ticket Tiers",
                            drafts = draftTicketTiers,
                            statsByTierId = stats?.ticketTierStats?.associateBy { it.ticketTierId }.orEmpty(),
                            theme = theme,
                            onDraftsChange = { draftTicketTiers = it },
                        )
                    }
                }

                eventTimeError(draftStartIso, draftEndIso)?.let { StatusBanner(it, theme, BannerTone.Error) }
                if (!draftTicketTiers.canSubmit()) StatusBanner("Each tier needs a name, a non-negative price with at most two decimals, and a valid capacity.", theme, BannerTone.Error)
                SectionCard(
                    title = "Ticket Overview",
                    headerActions = {
                        ActionButton("Refresh ticket details", { scope.launch { loadTicketData() } }, theme, isDisabled = isLoadingStats || isIssuing || isCreatingPaperBatch || releasingBatchId != null || isSaving, isPrimary = false)
                    },
                    description = "Review issued tickets and capacity, reserve paper stock, and export ticket codes. Issuance does not collect payment; completed sales are in Reports.",
                    theme = theme,
                ) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(16.px)
                        }
                    }) {
                        val soldCount = stats?.soldCount ?: 0
                        val usedCount = stats?.usedCount ?: 0
                        val reservedPaperCount = stats?.reservedPaperCount ?: 0
                        val remainingCount = stats?.remainingCount
                        val eventSoldOut = stats?.capacity != null && remainingCount == 0
                        val selectedTierStats = stats?.ticketTierStats?.firstOrNull { it.ticketTierId == selectedIssueTierId }
                        val selectedTierSoldOut = selectedTierStats?.capacity != null && selectedTierStats.remainingCount == 0
                        val selectedPaperTierStats = stats?.ticketTierStats?.firstOrNull { it.ticketTierId == selectedPaperTierId }
                        val selectedPaperTierFull = selectedPaperTierStats?.capacity != null && selectedPaperTierStats.remainingCount == 0
                        val paperBatchQuantityValue = paperBatchQuantity.toIntOrNull()

                        P({
                            style {
                                margin(0.px)
                                property("color", theme.onSurfaceVariant)
                                property("line-height", "1.5")
                            }
                        }) {
                            Text("${formatLocalDateTimeDisplay(currentEvent.startDate)} · ${currentEvent.location} · ${formatStartingPrice(currentEvent)} · ${formatCapacitySummary(currentEvent.capacity)}")
                        }
                        ticketLoadError?.let { StatusBanner(it, theme, BannerTone.Error) }
                        if (isLoadingStats) {
                            P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                Text("Loading event stats...")
                            }
                        } else if (ticketLoadError == null) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(10.px)
                                    property("flex-wrap", "wrap")
                                }
                            }) {
                                StatPill(label = "Tickets Issued", value = soldCount.toString(), theme = theme)
                                StatPill(label = "Checked In", value = usedCount.toString(), theme = theme)
                                StatPill(label = "Paper Reserved", value = reservedPaperCount.toString(), theme = theme)
                                StatPill(label = "Remaining", value = remainingCount?.toString() ?: "Unlimited", theme = theme)
                            }
                        }

                        if (eventSoldOut) {
                            StatusBanner(
                                message = "Live sales are currently sold out. Reserved paper batches may still be circulating until unused stock is released.",
                                tone = BannerTone.Info,
                                theme = theme,
                            )
                        }

                        issueError?.let { StatusBanner(it, tone = BannerTone.Error, theme = theme) }
                        issueSuccess?.let { ticket ->
                            StatusBanner(
                                message = "Issued ${ticket.ticketTierName} ticket: ${ticket.ticketCode}",
                                tone = BannerTone.Success,
                                theme = theme,
                            )
                        }
                        paperBatchError?.let { StatusBanner(it, tone = BannerTone.Error, theme = theme) }
                        paperBatchSuccess?.let { batchResponse ->
                            StatusBanner(
                                message = "Reserved ${batchResponse.batch.reservedQuantity} ${batchResponse.batch.ticketTierName} paper tickets in batch ${batchResponse.batch.id.take(8)}.",
                                tone = BannerTone.Success,
                                theme = theme,
                            )
                        }
                        paperBatchReleaseSuccess?.let { StatusBanner(it, tone = BannerTone.Success, theme = theme) }

                        if (currentEvent.ticketTiers.none { it.active }) {
                            StatusBanner(
                                message = "Activate at least one ticket tier before issuing live or paper tickets.",
                                tone = BannerTone.Info,
                                theme = theme,
                            )
                        } else {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(12.px)
                                }
                            }) {
                                P({ style { margin(0.px); property("color", theme.onSurface) ; property("font-weight", "700") } }) {
                                    Text("Single Ticket")
                                }
                                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                    Text("Issue one live ticket when you need a walk-up admission right away.")
                                }
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        gap(8.px)
                                        property("flex-wrap", "wrap")
                                    }
                                }) {
                                    currentEvent.ticketTiers.filter { it.active }.forEach { ticketTier ->
                                        val ticketTierStats = stats?.ticketTierStats?.firstOrNull { it.ticketTierId == ticketTier.id }
                                        ChoiceChip(
                                            label = tierSummary(ticketTier, ticketTierStats),
                                            isSelected = selectedIssueTierId == ticketTier.id,
                                            onClick = { selectedIssueTierId = ticketTier.id },
                                            theme = theme,
                                        )
                                    }
                                }
                                FieldRow {
                                    TextField(
                                        theme = theme,
                                        label = "Holder Name (optional)",
                                        value = issueHolderName,
                                        onValueChange = { issueHolderName = it },
                                        placeholder = "Walk-up guest",
                                    )
                                }
                                if (selectedTierSoldOut) {
                                    StatusBanner(
                                        message = "${selectedTierStats.ticketTierName} tier is sold out.",
                                        tone = BannerTone.Info,
                                        theme = theme,
                                    )
                                }
                                ActionButton(
                                    label = if (isIssuing) "Issuing..." else "Issue Ticket",
                                    onClick = { submitIssue() },
                                    isDisabled = isLoadingStats || ticketLoadError != null || isIssuing || selectedIssueTierId == null || eventSoldOut || selectedTierSoldOut,
                                    theme = theme,
                                )

                                Div({
                                    style {
                                        property("border-top", "1px solid ${theme.outlineVariant}")
                                        property("padding-top", "16px")
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(12.px)
                                    }
                                }) {
                                    P({ style { margin(0.px); property("color", theme.onSurface); property("font-weight", "700") } }) {
                                        Text("Paper Ticket Batch")
                                    }
                                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                        Text("Reserve a controlled paper batch for pre-printing. These tickets hold capacity immediately and stay grouped by batch for export.")
                                    }
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            gap(8.px)
                                            property("flex-wrap", "wrap")
                                        }
                                    }) {
                                        currentEvent.ticketTiers.filter { it.active }.forEach { ticketTier ->
                                            val ticketTierStats = stats?.ticketTierStats?.firstOrNull { it.ticketTierId == ticketTier.id }
                                            ChoiceChip(
                                                label = tierSummary(ticketTier, ticketTierStats),
                                                isSelected = selectedPaperTierId == ticketTier.id,
                                                onClick = { selectedPaperTierId = ticketTier.id },
                                                theme = theme,
                                            )
                                        }
                                    }
                                    FieldRow {
                                        TextField(
                                            theme = theme,
                                            label = "Reserve Quantity",
                                            value = paperBatchQuantity,
                                            inputTypeOverride = "number",
                                            onValueChange = { paperBatchQuantity = it },
                                        )
                                    }
                                    if (selectedPaperTierFull) {
                                        StatusBanner(
                                            message = "${selectedPaperTierStats.ticketTierName} tier has no remaining capacity to reserve.",
                                            tone = BannerTone.Info,
                                            theme = theme,
                                        )
                                    }
                                    ActionButton(
                                        label = if (isCreatingPaperBatch) "Reserving..." else "Reserve Paper Batch",
                                        onClick = { submitPaperBatch() },
                                        isDisabled = isLoadingStats || ticketLoadError != null || isCreatingPaperBatch || selectedPaperTierId == null || eventSoldOut || selectedPaperTierFull || paperBatchQuantityValue == null || paperBatchQuantityValue < 1,
                                        theme = theme,
                                    )
                                }
                            }
                        }

                        Div({
                            style {
                                property("border-top", "1px solid ${theme.outlineVariant}")
                                property("padding-top", "16px")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(12.px)
                            }
                        }) {
                            P({ style { margin(0.px); property("color", theme.onSurface); property("font-weight", "700") } }) {
                                Text("Paper Batches")
                            }
                            P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                Text("Review reserved stock, release unused tickets still in staff possession, and export batch codes for QR preparation.")
                            }
                            if (paperBatches.isEmpty() && !isLoadingStats && ticketLoadError == null) {
                                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                    Text("No paper ticket batches have been reserved for this event yet.")
                                }
                            } else {
                                paperBatches.forEach { batch ->
                                    val batchTickets = issuedTickets.filter { it.paperBatchId == batch.id }
                                    Div({
                                        style {
                                            padding(16.px)
                                            borderRadius(18.px)
                                            property("border", "1px solid ${theme.outlineVariant}")
                                            property("background-color", theme.surface)
                                            display(DisplayStyle.Flex)
                                            flexDirection(FlexDirection.Column)
                                            gap(12.px)
                                        }
                                    }) {
                                        H4({
                                            style {
                                                margin(0.px)
                                                property("color", theme.onSurface)
                                            }
                                        }) {
                                            Text("${batch.ticketTierName} · Batch ${batch.id.take(8)}")
                                        }
                                        P({
                                            style {
                                                margin(0.px)
                                                property("color", theme.onSurfaceVariant)
                                                property("line-height", "1.5")
                                            }
                                        }) {
                                            Text("Reserved ${batch.reservedQuantity} tickets · Created ${formatLocalDateTimeDisplay(batch.createdAt)} · Status ${batch.status.name}")
                                        }
                                        Div({
                                            style {
                                                display(DisplayStyle.Flex)
                                                gap(10.px)
                                                property("flex-wrap", "wrap")
                                            }
                                        }) {
                                            StatPill(label = "Remaining", value = batch.remainingQuantity.toString(), theme = theme)
                                            StatPill(label = "Admitted", value = batch.admittedQuantity.toString(), theme = theme)
                                            StatPill(label = "Released", value = batch.releasedQuantity.toString(), theme = theme)
                                        }
                                        FieldRow {
                                            TextField(
                                                theme = theme,
                                                label = "Release Unused",
                                                value = releaseQuantities[batch.id] ?: batch.remainingQuantity.toString(),
                                                inputTypeOverride = "number",
                                                onValueChange = { value ->
                                                    releaseQuantities = releaseQuantities + (batch.id to value)
                                                },
                                            )
                                        }
                                        Div({
                                            style {
                                                display(DisplayStyle.Flex)
                                                gap(10.px)
                                                property("flex-wrap", "wrap")
                                            }
                                        }) {
                                            ActionButton(
                                                label = "Export CSV",
                                                onClick = { exportBatchCsv(batch) },
                                                isPrimary = false,
                                                isDisabled = batchTickets.isEmpty(),
                                                theme = theme,
                                            )
                                            ActionButton(
                                                label = if (releasingBatchId == batch.id) "Releasing..." else "Release Unused",
                                                onClick = { submitPaperRelease(batch) },
                                                isDisabled = releasingBatchId != null || batch.remainingQuantity == 0,
                                                theme = theme,
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        Div({
                            style {
                                property("border-top", "1px solid ${theme.outlineVariant}")
                                property("padding-top", "16px")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(12.px)
                            }
                        }) {
                            P({ style { margin(0.px); property("color", theme.onSurface); property("font-weight", "700") } }) {
                                Text("Issued Tickets")
                            }
                            P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                Text("Use this list to confirm ticket codes, status, and paper batch grouping before printing or reconciliation.")
                            }
                            if (issuedTickets.isNotEmpty()) {
                                ActionButton(
                                    label = "Export All Tickets CSV",
                                    onClick = { exportIssuedTicketsCsv() },
                                    isPrimary = false,
                                    theme = theme,
                                )
                            }
                            if (issuedTickets.isEmpty() && !isLoadingStats && ticketLoadError == null) {
                                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                    Text("No tickets have been issued for this event yet.")
                                }
                            } else {
                                issuedTickets.take(120).forEach { ticket ->
                                    Div({
                                        style {
                                            padding(14.px)
                                            borderRadius(16.px)
                                            property("border", "1px solid ${theme.outlineVariant}")
                                            property("background-color", theme.surfaceContainerHighest)
                                            display(DisplayStyle.Flex)
                                            flexDirection(FlexDirection.Column)
                                            gap(6.px)
                                        }
                                    }) {
                                        P({
                                            style {
                                                margin(0.px)
                                                property("color", theme.onSurface)
                                                property("font-weight", "700")
                                            }
                                        }) {
                                            Text(ticket.ticketCode)
                                        }
                                        P({
                                            style {
                                                margin(0.px)
                                                property("color", theme.onSurfaceVariant)
                                                property("line-height", "1.5")
                                            }
                                        }) {
                                            Text("${ticket.ticketTierName} · ${formatTicketStatusLabel(ticket.status)} · ${ticket.paperBatchId?.let { "Batch ${it.take(8)}" } ?: "Live Issued"}")
                                        }
                                        P({
                                            style {
                                                margin(0.px)
                                                property("color", theme.onSurfaceVariant)
                                            }
                                        }) {
                                            Text("Created ${formatLocalDateTimeDisplay(ticket.createdAt)}${ticket.holderName?.let { " · $it" } ?: ""}")
                                        }
                                    }
                                }
                                if (issuedTickets.size > 120) {
                                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                                        Text("Showing the most recent 120 tickets here. Use CSV export for the full event list.")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Div({
                style {
                    property("padding", "16px 24px 24px")
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    property("justify-content", "flex-end")
                    property("flex-wrap", "wrap")
                    property("border-top", "1px solid ${theme.outlineVariant}")
                    property("margin-top", "16px")
                    property("background-color", theme.surfaceContainerHigh)
                }
            }) {
                ActionButton(
                    label = if (draftActive) "Archive Event" else "Restore Event",
                    onClick = { submitArchiveToggle() },
                    isPrimary = false,
                    isDisabled = isSaving,
                    theme = theme,
                )
                ActionButton(
                    label = "Close",
                    onClick = onClose,
                    isPrimary = false,
                    theme = theme,
                )
                ActionButton(
                    label = if (isSaving) "Saving..." else "Save Event",
                    onClick = { submitSave() },
                    isDisabled = !canSave,
                    theme = theme,
                )
            }
        }
    }
}

@Composable
private fun LargePickerField(
    theme: MaterialThemePalette,
    label: String,
    value: String,
    inputTypeOverride: String,
    step: String? = null,
    onValueChange: (String) -> Unit,
) {
    TextField(
        theme = theme,
        label = label,
        value = value,
        inputTypeOverride = inputTypeOverride,
        step = step,
        inputPaddingVerticalPx = 16,
        inputPaddingHorizontalPx = 18,
        inputFontSize = "1.05rem",
        minHeightPx = 60,
        onValueChange = onValueChange,
    )
}

@Composable
private fun AlarmTimePickerField(
    theme: MaterialThemePalette,
    label: String,
    value: TimePickerDraft,
    onValueChange: (TimePickerDraft) -> Unit,
) {
    var isDialogOpen by remember { mutableStateOf(false) }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            property("flex", "1")
            property("min-width", "0")
        }
    }) {
        P({
            style {
                margin(0.px)
                property("color", theme.onSurfaceVariant)
                property("font-size", "0.8rem")
                property("font-weight", "700")
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
            }
        }) {
            Text(label)
        }

        Div({
            style {
                padding(16.px, 18.px)
                borderRadius(14.px)
                property("box-sizing", "border-box")
                property("width", "100%")
                property("min-height", "60px")
                property("background-color", theme.surface)
                property("color", theme.onSurface)
                property("border", "1px solid ${theme.outline}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                display(DisplayStyle.Flex)
                property("align-items", "center")
                gap(10.px)
                property("flex-wrap", "wrap")
                property("cursor", "pointer")
            }
            onClick { isDialogOpen = true }
        }) {
            TimeSegmentButton(
                theme = theme,
                label = value.hour?.toString() ?: "HH",
                minWidthPx = 72,
                onClick = { isDialogOpen = true },
            )
            P({
                style {
                    margin(0.px)
                    property("color", theme.onSurface)
                    property("font-size", "1.1rem")
                    property("font-weight", "700")
                }
            }) {
                Text(":")
            }
            TimeSegmentButton(
                theme = theme,
                label = value.minute?.toString()?.padStart(2, '0') ?: "MM",
                minWidthPx = 86,
                onClick = { isDialogOpen = true },
            )
            TimeSegmentButton(
                theme = theme,
                label = value.meridiem.name,
                minWidthPx = 76,
                onClick = { isDialogOpen = true },
            )
        }
    }

    if (isDialogOpen) {
        WheelTimePickerDialog(
            theme = theme,
            value = value,
            onCancel = { isDialogOpen = false },
            onApply = {
                onValueChange(it)
                isDialogOpen = false
            },
        )
    }
}

@Composable
private fun TimeSegmentButton(
    theme: MaterialThemePalette,
    label: String,
    minWidthPx: Int,
    onClick: () -> Unit,
) {
    Button(attrs = {
        onClick { onClick() }
        style {
            padding(10.px, 14.px)
            borderRadius(14.px)
            property("min-width", "${minWidthPx}px")
            property("border", "1px solid ${theme.outline}")
            property("background-color", theme.surfaceContainerHighest)
            property("color", theme.onSurface)
            property("font-size", "1.02rem")
            property("font-weight", "700")
            property("cursor", "pointer")
        }
    }) {
        Text(label)
    }
}

@Composable
private fun WheelTimePickerDialog(
    theme: MaterialThemePalette,
    value: TimePickerDraft,
    onCancel: () -> Unit,
    onApply: (TimePickerDraft) -> Unit,
) {
    val initialValue = remember(value) { value.withCurrentTimeDefaults() }
    var selectedHour by remember(initialValue) { mutableStateOf(initialValue.hour ?: 12) }
    var selectedMinute by remember(initialValue) { mutableStateOf(initialValue.minute ?: 0) }
    var selectedMeridiem by remember(initialValue) { mutableStateOf(initialValue.meridiem) }

    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "24px")
                property("background", "rgba(15, 23, 42, 0.24)")
                property("backdrop-filter", "blur(10px)")
                property("z-index", "1100")
            }
            onClick { onCancel() }
        }
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("width", "min(100%, 420px)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "16px")
                    padding(22.px)
                    borderRadius(28.px)
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                    )
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("box-shadow", "0 24px 60px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                }
            }
        ) {
            H3({
                style {
                    margin(0.px)
                    property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                    property("font-size", "1.4rem")
                    property("letter-spacing", "-0.03em")
                }
            }) {
                Text("Choose Time")
            }

            P({
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.5")
                }
            }) {
                Text("Scroll each wheel until the selected value sits in the center band.")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(14.px)
                    property("justify-content", "center")
                    property("align-items", "stretch")
                    property("flex-wrap", "wrap")
                }
            }) {
                WheelPickerColumn(
                    id = "time-wheel-hour",
                    theme = theme,
                    label = "Hour",
                    options = (1..12).map { WheelOption(value = it, label = it.toString()) },
                    selectedValue = selectedHour,
                    onValueChange = { selectedHour = it },
                )
                WheelPickerColumn(
                    id = "time-wheel-minute",
                    theme = theme,
                    label = "Minute",
                    options = (0..59).map { WheelOption(value = it, label = it.toString().padStart(2, '0')) },
                    selectedValue = selectedMinute,
                    onValueChange = { selectedMinute = it },
                )
                WheelPickerColumn(
                    id = "time-wheel-meridiem",
                    theme = theme,
                    label = "AM / PM",
                    options = TimeMeridiem.entries.map { WheelOption(value = it, label = it.name) },
                    selectedValue = selectedMeridiem,
                    onValueChange = { selectedMeridiem = it },
                )
            }

            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "flex-end")
                    property("gap", "10px")
                    property("flex-wrap", "wrap")
                }
            }) {
                ActionButton(
                    label = "Cancel",
                    onClick = onCancel,
                    theme = theme,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Apply Time",
                    onClick = {
                        onApply(
                            TimePickerDraft(
                                hour = selectedHour,
                                minute = selectedMinute,
                                meridiem = selectedMeridiem,
                            )
                        )
                    },
                    theme = theme,
                )
            }
        }
    }
}

@Composable
private fun <T> WheelPickerColumn(
    id: String,
    theme: MaterialThemePalette,
    label: String,
    options: List<WheelOption<T>>,
    selectedValue: T,
    onValueChange: (T) -> Unit,
) {
    val selectedIndex = options.indexOfFirst { it.value == selectedValue }.coerceAtLeast(0)
    var snapTimeoutId by remember(id) { mutableStateOf<Int?>(null) }

    fun alignWheelTo(index: Int) {
        window.setTimeout({
            val element = document.getElementById(id) as? HTMLElement ?: return@setTimeout
            element.scrollTop = index.coerceIn(0, options.lastIndex) * TimeWheelRowHeightPx.toDouble()
        }, 0)
    }

    LaunchedEffect(id, selectedIndex) {
        val element = document.getElementById(id) as? HTMLElement
        val targetTop = selectedIndex * TimeWheelRowHeightPx.toDouble()
        if (element == null || abs(element.scrollTop - targetTop) > 1.0) {
            alignWheelTo(selectedIndex)
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
            property("min-width", "96px")
            property("flex", "1 1 96px")
            property("max-width", "132px")
        }
    }) {
        P({
            style {
                margin(0.px)
                property("color", theme.onSurfaceVariant)
                property("font-size", "0.75rem")
                property("font-weight", "700")
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
                property("text-align", "center")
            }
        }) {
            Text(label)
        }

        Div({
            style {
                property("position", "relative")
                property("height", "${TimeWheelRowHeightPx * TimeWheelVisibleRows}px")
                borderRadius(22.px)
                property("border", "1px solid ${theme.outlineVariant}")
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                )
                property("box-shadow", "0 18px 34px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    property("position", "absolute")
                    property("left", "10px")
                    property("right", "10px")
                    property("top", "calc(50% - ${TimeWheelRowHeightPx / 2}px)")
                    property("height", "${TimeWheelRowHeightPx}px")
                    property("border-radius", "18px")
                    property("border", "1px solid ${theme.primary}")
                    property("background", theme.secondaryContainer)
                    property("opacity", "0.82")
                    property("pointer-events", "none")
                    property("z-index", "1")
                }
            })

            Div({
                style {
                    property("position", "absolute")
                    property("left", "0")
                    property("right", "0")
                    property("top", "0")
                    property("height", "${TimeWheelRowHeightPx * TimeWheelSidePaddingRows}px")
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surface} 0%, rgba(255,255,255,0) 100%)"
                    )
                    property("pointer-events", "none")
                    property("z-index", "2")
                }
            })

            Div({
                style {
                    property("position", "absolute")
                    property("left", "0")
                    property("right", "0")
                    property("bottom", "0")
                    property("height", "${TimeWheelRowHeightPx * TimeWheelSidePaddingRows}px")
                    property(
                        "background",
                        "linear-gradient(0deg, ${theme.surface} 0%, rgba(255,255,255,0) 100%)"
                    )
                    property("pointer-events", "none")
                    property("z-index", "2")
                }
            })

            Div(attrs = {
                id(id)
                onScroll {
                    val element = document.getElementById(id) as? HTMLElement ?: return@onScroll
                    val nearestIndex = (element.scrollTop / TimeWheelRowHeightPx).roundToInt().coerceIn(0, options.lastIndex)
                    val nearestValue = options[nearestIndex].value
                    if (nearestValue != selectedValue) {
                        onValueChange(nearestValue)
                    }
                    snapTimeoutId?.let { timeoutId -> window.clearTimeout(timeoutId) }
                    snapTimeoutId = window.setTimeout({
                        val currentElement = document.getElementById(id) as? HTMLElement ?: return@setTimeout
                        currentElement.scrollTop = nearestIndex * TimeWheelRowHeightPx.toDouble()
                    }, 90)
                }
                style {
                    property("height", "100%")
                    property("overflow-y", "auto")
                    property("scroll-snap-type", "y mandatory")
                    property("scrollbar-width", "none")
                    property("-ms-overflow-style", "none")
                    property("position", "relative")
                    property("z-index", "0")
                }
            }) {
                repeat(TimeWheelSidePaddingRows) {
                    Div({
                        style {
                            property("height", "${TimeWheelRowHeightPx}px")
                            property("scroll-snap-align", "center")
                        }
                    })
                }

                options.forEachIndexed { index, option ->
                    val isSelected = option.value == selectedValue
                    Div(attrs = {
                        onClick {
                            onValueChange(option.value)
                            alignWheelTo(index)
                        }
                        style {
                            property("height", "${TimeWheelRowHeightPx}px")
                            display(DisplayStyle.Flex)
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("scroll-snap-align", "center")
                            property("cursor", "pointer")
                            property("color", if (isSelected) theme.onSecondaryContainer else theme.onSurfaceVariant)
                            property("font-size", if (isSelected) "1.08rem" else "0.98rem")
                            property("font-weight", if (isSelected) "700" else "500")
                            property("opacity", if (isSelected) "1" else "0.72")
                            property("transition", "color 120ms ease, opacity 120ms ease, font-size 120ms ease")
                            property("user-select", "none")
                        }
                    }) {
                        Text(option.label)
                    }
                }

                repeat(TimeWheelSidePaddingRows) {
                    Div({
                        style {
                            property("height", "${TimeWheelRowHeightPx}px")
                            property("scroll-snap-align", "center")
                        }
                    })
                }
            }
        }
    }
}

@Composable
private fun CreateEventRequirementsPanel(
    theme: MaterialThemePalette,
    missingRequirements: List<String>,
) {
    Div({
        style {
            padding(16.px)
            borderRadius(22.px)
            property("border", "1px solid ${theme.outlineVariant}")
            property("background-color", theme.surfaceContainerHighest)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
        }
    }) {
        H4({ style { margin(0.px); property("color", theme.onSurface) } }) {
            Text("Before You Can Create")
        }
        P({
            style {
                margin(0.px)
                property("color", theme.onSurfaceVariant)
                property("line-height", "1.5")
            }
        }) {
            Text("Required: event name, location, start date, start time, end date, end time, and at least one ticket tier with a valid price.")
        }

        if (missingRequirements.isEmpty()) {
            P({
                style {
                    margin(0.px)
                    property("color", theme.onSurface)
                    property("font-weight", "700")
                }
            }) {
                Text("Ready to create. All required fields are complete.")
            }
        } else {
            P({
                style {
                    margin(0.px)
                    property("color", theme.onSurface)
                    property("font-weight", "700")
                }
            }) {
                Text("Still needed:")
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                }
            }) {
                missingRequirements.forEach { requirement ->
                    P({
                        style {
                            margin(0.px)
                            property("color", theme.onSurfaceVariant)
                        }
                    }) {
                        Text("• $requirement")
                    }
                }
            }
        }
    }
}

@Composable
private fun CapacityField(
    theme: MaterialThemePalette,
    label: String,
    capacity: Int?,
    supportingText: String? = null,
    dialogTitle: String,
    dialogSubjectLabel: String,
    onCapacityChange: (Int?) -> Unit,
) {
    var dialogState by remember { mutableStateOf<CapacityDialogState?>(null) }
    val canDecrement = capacity != null && capacity > 1

    fun openDialog(initialDraft: String = capacity?.toString().orEmpty()) {
        dialogState = CapacityDialogState(
            currentCapacity = capacity,
            initialDraft = initialDraft,
        )
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            property("flex", "1")
            property("min-width", "0")
        }
    }) {
        P({
            style {
                margin(0.px)
                property("color", theme.onSurfaceVariant)
                property("font-size", "0.8rem")
                property("font-weight", "700")
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
            }
        }) {
            Text(label)
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                property("align-items", "center")
                gap(10.px)
                property("flex-wrap", "wrap")
            }
        }) {
            CapacityStepButton(
                label = "-",
                theme = theme,
                isDisabled = !canDecrement,
                onClick = {
                    if (capacity != null && capacity > 1) {
                        onCapacityChange(capacity - 1)
                    }
                },
            )

            CapacityDisplayButton(
                label = capacity?.toString() ?: "Unlimited",
                theme = theme,
                onClick = { openDialog() },
            )

            CapacityStepButton(
                label = "+",
                theme = theme,
                isDisabled = false,
                onClick = {
                    if (capacity == null) {
                        onCapacityChange(1)
                    } else {
                        onCapacityChange(capacity + 1)
                    }
                },
            )
        }

        supportingText?.let { hint ->
            P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                Text(hint)
            }
        }
    }

    dialogState?.let { dialog ->
        CapacitySetDialog(
            currentCapacity = dialog.currentCapacity,
            initialDraft = dialog.initialDraft,
            theme = theme,
            title = dialogTitle,
            subjectLabel = dialogSubjectLabel,
            onCancel = { dialogState = null },
            onApply = { updatedCapacity ->
                onCapacityChange(updatedCapacity)
                dialogState = null
            },
        )
    }
}

@Composable
private fun TicketTierEditor(
    title: String,
    drafts: List<TicketTierDraft>,
    statsByTierId: Map<String, EventTicketTierStatsDto> = emptyMap(),
    theme: MaterialThemePalette,
    onDraftsChange: (List<TicketTierDraft>) -> Unit,
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(14.px)
        }
    }) {
        H4({ style { margin(0.px); property("color", theme.onSurface) } }) {
            Text(title)
        }
        P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
            Text("Define multiple prices here. Use the tier capacity control for small changes, or tap the value to set an exact cap or switch to Unlimited. Inactive tiers stay available for history but stop new issuance.")
        }

        drafts.forEachIndexed { index, draft ->
            val ticketTierStats = draft.id?.let { statsByTierId[it] }
            Div({
                style {
                    padding(18.px)
                    borderRadius(24.px)
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("background-color", theme.surface)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(14.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("gap", "10px")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    H4({ style { margin(0.px); property("color", theme.onSurface) } }) {
                        Text("Tier ${index + 1}")
                    }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(8.px)
                            property("flex-wrap", "wrap")
                        }
                    }) {
                        ChoiceChip(
                            label = if (draft.active) "Active" else "Inactive",
                            isSelected = draft.active,
                            onClick = {
                                onDraftsChange(
                                    drafts.updateAt(index) { existing -> existing.copy(active = !existing.active) }
                                )
                            },
                            theme = theme,
                        )
                        ActionButton(
                            label = if (draft.id == null) "Remove Tier" else "Archive in Form",
                            onClick = {
                                onDraftsChange(
                                    if (draft.id == null && drafts.size > 1) {
                                        drafts.filterIndexed { currentIndex, _ -> currentIndex != index }
                                    } else {
                                        drafts.updateAt(index) { existing -> existing.copy(active = false) }
                                    }
                                )
                            },
                            theme = theme,
                            isPrimary = false,
                            isDisabled = drafts.size == 1 && draft.id == null,
                        )
                    }
                }

                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Tier Name",
                        value = draft.name,
                        onValueChange = { value ->
                            onDraftsChange(drafts.updateAt(index) { existing -> existing.copy(name = value) })
                        },
                        placeholder = "General Admission",
                    )
                    TextField(
                        theme = theme,
                        label = "Price",
                        value = draft.priceInput,
                        onValueChange = { value ->
                            onDraftsChange(drafts.updateAt(index) { existing -> existing.copy(priceInput = value) })
                        },
                        placeholder = "5.00",
                    )
                    CapacityField(
                        theme = theme,
                        label = "Tier Capacity",
                        capacity = parseCapacityInput(draft.capacityInput),
                        dialogTitle = "Set Tier Capacity",
                        dialogSubjectLabel = "tier",
                        onCapacityChange = { updatedCapacity ->
                            onDraftsChange(
                                drafts.updateAt(index) { existing ->
                                    existing.copy(capacityInput = formatCapacityInput(updatedCapacity))
                                }
                            )
                        },
                    )
                }

                ticketTierStats?.let { tierStats ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(10.px)
                            property("flex-wrap", "wrap")
                        }
                    }) {
                        StatPill(label = "Sold", value = tierStats.soldCount.toString(), theme = theme)
                        StatPill(label = "Remaining", value = tierStats.remainingCount?.toString() ?: "Unlimited", theme = theme)
                    }
                    if (tierStats.capacity != null && tierStats.remainingCount == 0) {
                        StatusBanner(
                            message = "${tierStats.ticketTierName} tier is sold out.",
                            tone = BannerTone.Info,
                            theme = theme,
                        )
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                    }
                }) {
                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                        Text("Ticket extras · deferred for launch")
                    }
                    if (draft.entitlements.isEmpty()) {
                        P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                            Text("Concession credits and free-item redemption are not part of launch admission. Leave extras empty; existing stored extras are preserved.")
                        }
                    } else {
                        draft.entitlements.forEachIndexed { entitlementIndex, entitlementDraft ->
                            EntitlementEditor(
                                draft = entitlementDraft,
                                theme = theme,
                                onChange = { updatedEntitlement ->
                                    onDraftsChange(
                                        drafts.updateAt(index) { currentTier ->
                                            currentTier.copy(
                                                entitlements = currentTier.entitlements.updateAt(entitlementIndex) { updatedEntitlement }
                                            )
                                        }
                                    )
                                },
                                onRemove = {
                                    onDraftsChange(
                                        drafts.updateAt(index) { currentTier ->
                                            currentTier.copy(
                                                entitlements = currentTier.entitlements.filterIndexed { currentEntitlementIndex, _ ->
                                                    currentEntitlementIndex != entitlementIndex
                                                }
                                            )
                                        }
                                    )
                                },
                            )
                        }
                    }
                    ActionButton(
                        label = "Add Entitlement (deferred)",
                        isDisabled = true,
                        onClick = {
                            onDraftsChange(
                                drafts.updateAt(index) { currentTier ->
                                    currentTier.copy(entitlements = currentTier.entitlements + TicketEntitlementDraft())
                                }
                            )
                        },
                        isPrimary = false,
                        theme = theme,
                    )
                }
            }
        }

        ActionButton(
            label = "Add Ticket Tier",
            onClick = { onDraftsChange(drafts + defaultTicketTierDraft()) },
            isPrimary = false,
            theme = theme,
        )
    }
}

@Composable
private fun CapacityStepButton(
    label: String,
    theme: MaterialThemePalette,
    isDisabled: Boolean,
    onClick: () -> Unit,
) {
    var isPressed by remember(isDisabled) { mutableStateOf(false) }

    Button(
        attrs = {
            if (isDisabled) disabled()
            onClick { onClick() }
            onMouseDown { if (!isDisabled) isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { if (!isDisabled) isPressed = true }
            onTouchEnd { isPressed = false }
            onTouchCancel { isPressed = false }
            onBlur { isPressed = false }
            style {
                padding(0.px)
                property("width", "44px")
                property("height", "44px")
                property("border-radius", "999px")
                property("border", "1px solid ${theme.outline}")
                property("background-color", theme.surfaceContainerHighest)
                property("color", theme.onSurface)
                property("font-size", "1.2rem")
                property("font-weight", "700")
                property(
                    "box-shadow",
                    if (isPressed && !isDisabled) {
                        "0 2px 8px ${theme.shadow}"
                    } else {
                        "0 8px 18px ${theme.shadow}"
                    }
                )
                property(
                    "transform",
                    if (isPressed && !isDisabled) "translateY(1px)" else "translateY(0)"
                )
                property("transition", "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease")
                property("cursor", if (isDisabled) "default" else "pointer")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
                if (isDisabled) {
                    property("opacity", "0.5")
                }
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun CapacityDisplayButton(
    label: String,
    theme: MaterialThemePalette,
    onClick: () -> Unit,
) {
    var isPressed by remember { mutableStateOf(false) }

    Button(
        attrs = {
            onClick { onClick() }
            onMouseDown { isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { isPressed = true }
            onTouchEnd { isPressed = false }
            onTouchCancel { isPressed = false }
            onBlur { isPressed = false }
            style {
                padding(10.px, 16.px)
                borderRadius(999.px)
                property("min-width", "124px")
                property("border", "1px solid ${theme.outline}")
                property("background-color", theme.secondaryContainer)
                property("color", theme.onSecondaryContainer)
                property("font-weight", "700")
                property("letter-spacing", "-0.01em")
                property(
                    "box-shadow",
                    if (isPressed) {
                        "0 2px 8px ${theme.shadow}"
                    } else {
                        "0 8px 18px ${theme.shadow}"
                    }
                )
                property(
                    "transform",
                    if (isPressed) "translateY(1px)" else "translateY(0)"
                )
                property("transition", "transform 120ms ease, box-shadow 120ms ease")
                property("cursor", "pointer")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun CapacitySetDialog(
    currentCapacity: Int?,
    initialDraft: String,
    theme: MaterialThemePalette,
    title: String,
    subjectLabel: String,
    onCancel: () -> Unit,
    onApply: (Int?) -> Unit,
) {
    var capacityDraft by remember(initialDraft) { mutableStateOf(initialDraft) }
    var dialogError by remember(initialDraft) { mutableStateOf<String?>(null) }

    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "24px")
                property("background", "rgba(15, 23, 42, 0.24)")
                property("backdrop-filter", "blur(10px)")
                property("z-index", "1100")
            }
            onClick { onCancel() }
        }
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("width", "min(100%, 420px)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "16px")
                    padding(22.px)
                    borderRadius(28.px)
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                    )
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("box-shadow", "0 24px 60px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                }
            }
        ) {
            H3({
                style {
                    margin(0.px)
                    property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                    property("font-size", "1.4rem")
                    property("letter-spacing", "-0.03em")
                }
            }) {
                Text(title)
            }

            P({
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.5")
                }
            }) {
                Text(
                    if (currentCapacity == null) {
                        "This $subjectLabel is currently Unlimited. Choose Unlimited again or set an exact cap."
                    } else {
                        "This $subjectLabel is currently capped at $currentCapacity tickets. Choose Unlimited or set a new exact cap."
                    }
                )
            }

            dialogError?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            TextField(
                theme = theme,
                label = "Exact Capacity",
                value = capacityDraft,
                onValueChange = {
                    capacityDraft = it
                    dialogError = null
                },
                placeholder = "Enter a whole number",
                inputTypeOverride = "number",
            )

            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "flex-end")
                    property("gap", "10px")
                    property("flex-wrap", "wrap")
                }
            }) {
                ActionButton(
                    label = "Cancel",
                    onClick = onCancel,
                    theme = theme,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Use Unlimited",
                    onClick = { onApply(null) },
                    theme = theme,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Apply Amount",
                    onClick = {
                        val parsedCapacity = capacityDraft.trim().toIntOrNull()
                        when {
                            parsedCapacity == null -> dialogError = "Enter a whole number capacity or choose Unlimited."
                            parsedCapacity < 1 -> dialogError = "Capacity must be at least 1. Use Unlimited if you do not want a cap."
                            else -> onApply(parsedCapacity)
                        }
                    },
                    theme = theme,
                )
            }
        }
    }
}

@Composable
private fun EntitlementEditor(
    draft: TicketEntitlementDraft,
    theme: MaterialThemePalette,
    onChange: (TicketEntitlementDraft) -> Unit,
    onRemove: () -> Unit,
) {
    Div({
        style {
            padding(14.px)
            borderRadius(20.px)
            property("background-color", theme.surfaceContainerHighest)
            property("border", "1px solid ${theme.outlineVariant}")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                property("flex-wrap", "wrap")
            }
        }) {
            TicketEntitlementType.entries.forEach { type ->
                ChoiceChip(
                    label = type.name.replace('_', ' '),
                    isSelected = draft.type == type,
                    onClick = { onChange(draft.copy(type = type)) },
                    theme = theme,
                )
            }
        }
        FieldRow {
            TextField(
                theme = theme,
                label = "Label",
                value = draft.label,
                onValueChange = { onChange(draft.copy(label = it)) },
                placeholder = "One free drink per event",
            )
        }
        FieldRow {
            TextField(
                theme = theme,
                label = "Amount (cents, optional)",
                value = draft.amountInput,
                onValueChange = { onChange(draft.copy(amountInput = it)) },
                placeholder = "500",
            )
            TextField(
                theme = theme,
                label = "Quantity (optional)",
                value = draft.quantityInput,
                onValueChange = { onChange(draft.copy(quantityInput = it)) },
                placeholder = "1",
            )
        }
        ActionButton(
            label = "Remove Entitlement",
            onClick = onRemove,
            isPrimary = false,
            theme = theme,
        )
    }
}

@Composable
private fun ChoiceChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    theme: MaterialThemePalette,
    isInteractive: Boolean = true,
) {
    Button(attrs = {
        if (isInteractive) {
            onClick { onClick() }
        }
        style {
            padding(8.px, 12.px)
            borderRadius(999.px)
            property("border", "1px solid ${if (isSelected) theme.primary else theme.outlineVariant}")
            property("background-color", if (isSelected) theme.primaryContainer else theme.surfaceContainerHighest)
            property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
            property("cursor", if (isInteractive) "pointer" else "default")
            property("max-width", "100%")
            property("white-space", "normal")
            property("text-align", "left")
        }
    }) {
        Text(label)
    }
}

@Composable
private fun StatPill(
    label: String,
    value: String,
    theme: MaterialThemePalette,
) {
    Div({
        style {
            padding(12.px, 14.px)
            borderRadius(16.px)
            property("background-color", theme.surfaceContainerHighest)
            property("border", "1px solid ${theme.outline}")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(4.px)
        }
    }) {
        P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
            Text(label)
        }
        P({ style { margin(0.px); property("color", theme.onSurface); property("font-weight", "700") } }) {
            Text(value)
        }
    }
}

private fun defaultTicketTierDraft() = TicketTierDraft(
    name = "General Admission",
    priceInput = "",
    capacityInput = "",
    active = true,
)

private fun TicketTierDto.toDraft() = TicketTierDraft(
    id = id,
    name = name,
    priceInput = formatMoney(priceAmount.toLong()),
    capacityInput = formatCapacityInput(capacity),
    active = active,
    entitlements = entitlements.map { it.toDraft() },
)

private fun TicketEntitlementDto.toDraft() = TicketEntitlementDraft(
    type = type,
    label = label,
    amountInput = amount?.toString().orEmpty(),
    quantityInput = quantity?.toString().orEmpty(),
)

private fun List<TicketTierDraft>.canSubmit(): Boolean =
    isNotEmpty() && all { it.canSubmit() }

private fun TicketTierDraft.canSubmit(): Boolean =
    name.isNotBlank() &&
        ticketPriceAmount(priceInput) != null &&
        isCapacityInputValid(capacityInput) &&
        entitlements.all { it.isValid() }

private fun TicketEntitlementDraft.isValid(): Boolean {
    if (label.isBlank() && amountInput.isBlank() && quantityInput.isBlank()) {
        return true
    }
    val amountIsValid = amountInput.isBlank() || amountInput.trim().toIntOrNull()?.let { it > 0 } == true
    val quantityIsValid = quantityInput.isBlank() || quantityInput.trim().toIntOrNull()?.let { it > 0 } == true
    return label.isNotBlank() && amountIsValid && quantityIsValid
}

private fun List<TicketTierDraft>.toCreateRequests(): List<CreateTicketTierRequest>? =
    if (!canSubmit()) {
        null
    } else {
        map { draft ->
            CreateTicketTierRequest(
                name = draft.name.trim(),
                priceAmount = ticketPriceAmount(draft.priceInput) ?: return null,
                capacity = parseCapacityInput(draft.capacityInput),
                active = draft.active,
                entitlements = draft.entitlements.mapNotNull { it.toDtoOrNull() },
            )
        }
    }

private fun List<TicketTierDraft>.toUpdateRequests(): List<UpdateTicketTierRequest>? =
    if (!canSubmit()) {
        null
    } else {
        map { draft ->
            UpdateTicketTierRequest(
                id = draft.id,
                name = draft.name.trim(),
                priceAmount = ticketPriceAmount(draft.priceInput) ?: return null,
                capacity = parseCapacityInput(draft.capacityInput),
                replaceCapacity = true,
                active = draft.active,
                entitlements = draft.entitlements.mapNotNull { it.toDtoOrNull() },
            )
        }
    }

private fun TicketEntitlementDraft.toDtoOrNull(): TicketEntitlementDto? {
    if (label.isBlank() && amountInput.isBlank() && quantityInput.isBlank()) {
        return null
    }
    return TicketEntitlementDto(
        type = type,
        label = label.trim(),
        amount = amountInput.trim().takeIf(String::isNotBlank)?.toIntOrNull(),
        quantity = quantityInput.trim().takeIf(String::isNotBlank)?.toIntOrNull(),
    )
}

private fun <T> List<T>.updateAt(index: Int, update: (T) -> T): List<T> =
    mapIndexed { currentIndex, value -> if (currentIndex == index) update(value) else value }

private fun buildCreateEventMissingRequirements(
    eventName: String,
    location: String,
    startDate: String,
    startTime: TimePickerDraft,
    endDate: String,
    endTime: TimePickerDraft,
    ticketTiers: List<TicketTierDraft>,
): List<String> {
    val missing = mutableListOf<String>()
    val startTimeValue = startTime.toLocalTimeValueOrNull()
    val endTimeValue = endTime.toLocalTimeValueOrNull()

    if (eventName.isBlank()) missing += "Enter an event name."
    if (location.isBlank()) missing += "Enter a location."
    if (startDate.isBlank()) missing += "Choose a start date."
    if (!startTime.isComplete()) missing += "Choose a start time."
    if (endDate.isBlank()) missing += "Choose an end date."
    if (!endTime.isComplete()) missing += "Choose an end time."

    if (
        startDate.isNotBlank() &&
        startTimeValue != null &&
        parseLocalDateAndTimeInputToIso(startDate, startTimeValue) == null
    ) {
        missing += "Start date and time must be valid."
    }

    if (
        endDate.isNotBlank() &&
        endTimeValue != null &&
        parseLocalDateAndTimeInputToIso(endDate, endTimeValue) == null
    ) {
        missing += "End date and time must be valid."
    }

    val startIso = startTimeValue?.let { parseLocalDateAndTimeInputToIso(startDate, it) }
    val endIso = endTimeValue?.let { parseLocalDateAndTimeInputToIso(endDate, it) }
    if (startIso != null && endIso != null) eventTimeError(startIso, endIso)?.let { missing += it }

    if (ticketTiers.isEmpty()) {
        missing += "Add at least one ticket tier."
    } else {
        ticketTiers.forEachIndexed { index, tier ->
            val tierNumber = index + 1
            if (tier.name.isBlank()) {
                missing += "Tier $tierNumber needs a name."
            }
            if (ticketPriceAmount(tier.priceInput) == null) {
                missing += "Tier $tierNumber needs a non-negative price with at most two decimal places."
            }
            if (!isCapacityInputValid(tier.capacityInput)) {
                missing += "Tier $tierNumber capacity must be a whole number or Unlimited."
            }
            if (tier.entitlements.any { !it.isValid() }) {
                missing += "Tier $tierNumber has an incomplete entitlement."
            }
        }
    }

    return missing
}

private fun TimePickerDraft.isComplete(): Boolean =
    hour != null && minute != null

private fun TimePickerDraft.withCurrentTimeDefaults(): TimePickerDraft {
    val fallback = browserCurrentTimeDraft()
    return TimePickerDraft(
        hour = hour ?: fallback.hour,
        minute = minute ?: fallback.minute,
        meridiem = if (hour == null && minute == null) fallback.meridiem else meridiem,
    )
}

private fun TimePickerDraft.toLocalTimeValueOrNull(): String? {
    val selectedHour = hour ?: return null
    val selectedMinute = minute ?: return null
    val hour24 = when (meridiem) {
        TimeMeridiem.AM -> if (selectedHour == 12) 0 else selectedHour
        TimeMeridiem.PM -> if (selectedHour == 12) 12 else selectedHour + 12
    }
    return "${hour24.toString().padStart(2, '0')}:${selectedMinute.toString().padStart(2, '0')}"
}

private fun timePickerDraftFromIso(value: String): TimePickerDraft =
    timePickerDraftFromLocalTime(formatLocalTimeInputFromIso(value))

private fun timePickerDraftFromLocalTime(value: String): TimePickerDraft {
    val normalized = value.trim()
    if (normalized.isEmpty()) return TimePickerDraft()

    val hour24 = normalized.substringBefore(':').toIntOrNull() ?: return TimePickerDraft()
    val minute = normalized.substringAfter(':').toIntOrNull() ?: return TimePickerDraft()
    if (hour24 !in 0..23 || minute !in 0..59) return TimePickerDraft()

    val meridiem = if (hour24 >= 12) TimeMeridiem.PM else TimeMeridiem.AM
    val hour12 = when (val normalizedHour = hour24 % 12) {
        0 -> 12
        else -> normalizedHour
    }

    return TimePickerDraft(
        hour = hour12,
        minute = minute,
        meridiem = meridiem,
    )
}

private fun browserCurrentTimeDraft(): TimePickerDraft {
    val now = Date()
    val hour24 = now.getHours()
    val minute = now.getMinutes()
    val meridiem = if (hour24 >= 12) TimeMeridiem.PM else TimeMeridiem.AM
    val hour12 = when (val normalizedHour = hour24 % 12) {
        0 -> 12
        else -> normalizedHour
    }
    return TimePickerDraft(
        hour = hour12,
        minute = minute,
        meridiem = meridiem,
    )
}

private fun parseCapacityInput(value: String): Int? =
    value.trim().takeIf(String::isNotEmpty)?.toIntOrNull()

private fun isCapacityInputValid(value: String): Boolean =
    value.isBlank() || value.trim().toIntOrNull()?.let { it >= 0 } == true

private fun formatCapacityInput(capacity: Int?): String = capacity?.toString().orEmpty()

private fun formatStartingPrice(event: EventDto): String =
    event.startingPriceAmount?.let { "From ${formatMoney(it.toLong())}" } ?: "No active ticket tiers"

private fun formatCapacitySummary(capacity: Int?): String =
    capacity?.let { "Cap $it" } ?: "Unlimited"

private fun tierSummary(
    ticketTier: TicketTierDto,
    ticketTierStats: EventTicketTierStatsDto? = null,
): String =
    buildString {
        append(ticketTier.name)
        append(" · ")
        append(formatMoney(ticketTier.priceAmount.toLong()))
        append(" · ")
        append(formatCapacitySummary(ticketTier.capacity))
        ticketTierStats?.let {
            append(" · ")
            append(
                if (it.capacity != null && it.remainingCount == 0) {
                    "sold out"
                } else {
                    "${it.soldCount} sold"
                }
            )
        }
        if (!ticketTier.active) {
            append(" · inactive")
        }
    }

private fun formatTicketStatusLabel(status: TicketStatus): String =
    when (status) {
        TicketStatus.VALID -> "Valid"
        TicketStatus.UNUSED -> "Unused"
        TicketStatus.INVALID -> "Invalid"
        TicketStatus.USED -> "Checked In"
        TicketStatus.ADMITTED -> "Admitted"
        TicketStatus.VOID -> "Void"
        TicketStatus.RELEASED -> "Released"
    }

private fun downloadCsv(filename: String, rows: List<Map<String, String>>) {
    if (rows.isEmpty()) return
    val headers = rows.flatMap { it.keys }.distinct()
    val csv = buildString {
        append(headers.joinToString(",") { it.csvEscape() })
        append('\n')
        rows.forEachIndexed { index, row ->
            append(headers.joinToString(",") { header -> (row[header] ?: "").csvEscape() })
            if (index != rows.lastIndex) {
                append('\n')
            }
        }
    }
    val href = "data:text/csv;charset=utf-8,${js("encodeURIComponent(csv)") as String}"
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = href
    anchor.download = filename
    document.body?.appendChild(anchor)
    anchor.click()
    document.body?.removeChild(anchor)
}

private fun String.csvEscape(): String =
    "\"${replace("\"", "\"\"")}\""

private fun String.slugifyForDownload(): String =
    lowercase()
        .replace(Regex("[^a-z0-9]+"), "-")
        .trim('-')
        .ifBlank { "tickets" }
